import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Icon, Text } from '@quintoandar-tokko/jopijs';
import styles from './styles.module.scss';
import ParticipantCard from '@/components/ParticipantCard';
import useOnClickOutside from '@/components/shared/Hooks';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function AgentBranchCell({
  agentId,
  agentName,
  branchName,
  image,
  relativeSelector = '',
  withImage = false,
  withBoldTitle = false,
  withBranch = false,
  modal,
  tooltipStyles = {},
  ...rest
}) {
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [cardPosition, setCardPosition] = useState(null);
  const [modalEl, setModalEl] = useState(null);
  const cellRef = useRef(null);
  const cardRef = useRef(null);
  const track = useTrackPerformanceTable();
  useOnClickOutside(cellRef, isCardOpen, setIsCardOpen);
  const thumbnail = image ? (
    <Box as="img" className={styles.participantImage} src={image} />
  ) : (
    <Icon icon="icon-agente" className={styles.participantIcon} />
  );

  useEffect(() => {
    if (!relativeSelector) return;

    const el = document.querySelector(relativeSelector);
    setModalEl(el);
  }, []);

  useLayoutEffect(() => {
    if (!isCardOpen || !cellRef.current || !modalEl) return;

    const cellRect = cellRef.current.getBoundingClientRect();
    const modalRect = modalEl.getBoundingClientRect();

    const top = cellRect.top - modalRect.top;
    const left = cellRect.left - modalRect.left;

    setCardPosition({ top, left });
  }, [isCardOpen, modalEl]);

  const shouldShowCard = isCardOpen && cardPosition && modalEl;
  const participant = {
    id: agentId,
    name: agentName,
    profilePicture: image,
    branchName,
  };

  if (!agentId) {
    return <Icon icon="icon-mostrarmenos" className={styles.emptyDataIcon} />;
  }

  const toggleOpenCard = () => {
    setIsCardOpen((prev) => !prev);
    if (track[modal]) track[modal](TRACK_ELEMENT.AGENT_NAME);
  };

  return (
    <>
      <Box
        ref={cellRef}
        className={styles.agentBranchCell}
        onClick={toggleOpenCard}
        {...rest}
      >
        {withImage && (
          <Box className={styles.participantImageWrapper}>{thumbnail}</Box>
        )}
        <Box className={styles.agentBranchCellInfo}>
          <Text
            className={styles.agentBranchCellTitle}
            data-bold={withBoldTitle}
          >
            {agentName}
          </Text>
          {withBranch && (
            <Text className={styles.agentBranchCellSubtitle}>{branchName}</Text>
          )}
        </Box>
      </Box>
      {shouldShowCard &&
        createPortal(
          <Box
            ref={cardRef}
            className={styles.pendingCardContainer}
            __css={{ left: cardPosition.left, top: cardPosition.top }}
            onMouseDown={(event) => event.stopPropagation()}
            {...tooltipStyles}
          >
            <ParticipantCard type="agent" data={participant} />
          </Box>,
          modalEl
        )}
    </>
  );
}
